import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Spinner } from "@/components/ui/spinner";

interface LoadingOverlayApi {
  /** Number of async tasks currently running. */
  pending: number;
  isLoading: boolean;
  message: string | null;
  /** Manually start a task; returns a stop function. */
  start: (message?: string) => () => void;
  /** Wrap any promise so the global overlay shows while it runs. */
  run: <T>(fn: () => T | Promise<T>, message?: string) => Promise<T>;
}

const Ctx = createContext<LoadingOverlayApi | null>(null);

export function LoadingOverlayProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const messages = useRef<string[]>([]);

  const start = useCallback((msg?: string) => {
    if (msg) {
      messages.current.push(msg);
      setMessage(msg);
    }
    setPending((c) => c + 1);
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      if (msg) {
        messages.current = messages.current.filter((m, i) => !(m === msg && i === messages.current.lastIndexOf(msg)));
        setMessage(messages.current[messages.current.length - 1] ?? null);
      }
      setPending((c) => Math.max(0, c - 1));
    };
  }, []);

  const run = useCallback(
    async <T,>(fn: () => T | Promise<T>, msg?: string): Promise<T> => {
      const stop = start(msg);
      try {
        return await fn();
      } finally {
        stop();
      }
    },
    [start],
  );

  const value = useMemo<LoadingOverlayApi>(
    () => ({ pending, isLoading: pending > 0, message, start, run }),
    [pending, message, start, run],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <GlobalLoadingOverlay visible={pending > 0} message={message} />
    </Ctx.Provider>
  );
}

function GlobalLoadingOverlay({ visible, message }: { visible: boolean; message: string | null }) {
  if (!visible) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/60 backdrop-blur-[2px] animate-fade-in"
      role="alertdialog"
      aria-busy="true"
      aria-label={message ?? "Working"}
    >
      <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-lg">
        <Spinner size="md" />
        <span className="text-sm font-medium text-foreground">{message ?? "Please wait…"}</span>
      </div>
    </div>
  );
}

/** Access the global loading overlay. Safe no-op fallback outside the provider. */
export function useGlobalLoading(): LoadingOverlayApi {
  const ctx = useContext(Ctx);
  if (ctx) return ctx;
  return {
    pending: 0,
    isLoading: false,
    message: null,
    start: () => () => {},
    run: async (fn) => fn(),
  };
}
